import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import HamburgerMenu from './HamburgerMenu';
import { useAuth } from './useAuth';
import useWebSocket from './useWebSocket';
import { fetchMergedAdsbAircraft } from './adsbExchange';
import {
    aircraftMatchesGroupPolicy,
    getAircraftTmsName,
    getGroupPolicyLabel,
    getTmsGroups,
} from './groupPolicy';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
        status: {
            UP: '#28a745',
            PMC: '#0070c0',
            XC: '#20c997',
            DN: '#dc3545',
            DS: '#dc3545',
            PM: '#ffc107',
            FCF: '#8716bbff',
            AWP: '#335d80ff',
            OTH: '#6c757d',
            DET: '#6c757d',
            AIMD: '#6c757d',
            ACI: '#6c757d',
            REI: '#6c757d'
        }
    };

    return {
        page: { backgroundColor: colors.bg, minHeight: '100vh', padding: '20px' },
        container: { maxWidth: '1600px', margin: '0 auto', fontFamily: 'sans-serif' },
        pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
        pageTitle: { fontSize: '24px', margin: 0, color: colors.text },
        statusSection: {
            marginBottom: '20px',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: `0 2px 5px ${colors.shadow}`
        },
        statusHeader: {
            padding: '15px 20px',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            userSelect: 'none'
        },
        statusHeaderLeft: {
            display: 'flex',
            alignItems: 'center',
            flex: '0 0 auto'
        },
        statusHeaderMiddle: {
            flex: '1 1 auto',
            textAlign: 'center',
            fontSize: '14px',
            opacity: 0.9
        },
        statusHeaderRight: {
            flex: '0 0 auto',
            fontSize: '14px',
            opacity: 0.9
        },
        caret: {
            fontSize: '16px',
            marginRight: '10px',
            transition: 'transform 0.2s ease',
            display: 'inline-block'
        },
        statusTitle: {
            fontSize: '18px',
            fontWeight: 'bold'
        },
        statusStats: {
            fontSize: '14px',
            opacity: 0.9
        },
        aircraftGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: '8px',
            padding: '15px 20px',
            backgroundColor: colors.cardBg
        },
        aircraftItem: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '8px',
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            fontSize: '12px',
            textAlign: 'center'
        },
        aircraftItemHighlighted: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '8px',
            backgroundColor: isDark ? '#4a1a1a' : '#ffebee',
            borderRadius: '4px',
            border: `1px solid #f44336`,
            fontSize: '12px',
            textAlign: 'center'
        },
        aircraftItemWarning: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '8px',
            backgroundColor: isDark ? '#4a3a1a' : '#fff3e0',
            borderRadius: '4px',
            border: `1px solid #ff9800`,
            fontSize: '12px',
            textAlign: 'center'
        },
        aircraftItemCritical: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '8px',
            backgroundColor: isDark ? '#1a1a1a' : '#f3e5f5',
            borderRadius: '4px',
            border: `1px solid #9c27b0`,
            fontSize: '12px',
            textAlign: 'center'
        },
        buno: {
            fontSize: '14px',
            fontWeight: 'bold',
            color: isDark ? '#ffffff' : colors.text,
            marginBottom: '2px'
        },
        bunoHighlighted: {
            fontSize: '14px',
            fontWeight: 'bold',
            color: isDark ? '#ffcdd2' : '#d32f2f',
            marginBottom: '2px'
        },
        bunoWarning: {
            fontSize: '14px',
            fontWeight: 'bold',
            color: isDark ? '#ffe0b2' : '#f57c00',
            marginBottom: '2px'
        },
        lfd: {
            fontSize: '11px',
            color: isDark ? '#e0e0e0' : colors.subtleText
        },
        lfdHighlighted: {
            fontSize: '11px',
            color: isDark ? '#ffcdd2' : '#d32f2f'
        },
        lfdWarning: {
            fontSize: '11px',
            color: isDark ? '#ffe0b2' : '#f57c00'
        },
        bunoCritical: {
            fontSize: '14px',
            fontWeight: 'bold',
            color: isDark ? '#00e5ff' : '#7b1fa2',
            marginBottom: '2px'
        },
        lfdCritical: {
            fontSize: '11px',
            color: isDark ? '#00e5ff' : '#7b1fa2'
        },
        noAircraft: {
            textAlign: 'center',
            color: colors.subtleText,
            fontStyle: 'italic',
            padding: '20px',
            fontSize: '14px'
        },
        totalSummary: {
            backgroundColor: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: `0 2px 5px ${colors.shadow}`
        },
        totalTitle: { fontSize: '20px', fontWeight: 'bold', color: colors.text, marginBottom: '10px' },
        totalGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' },
        totalItem: { textAlign: 'center' },
        totalNumber: { fontSize: '20px', fontWeight: 'bold', color: colors.text },
        totalPercentage: { fontSize: '14px', fontWeight: 'bold', color: colors.subtleText, marginTop: '2px' },
        totalLabel: { fontSize: '12px', color: colors.subtleText, marginTop: '5px' },
        lfdKey: {
            backgroundColor: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px',
            boxShadow: `0 2px 5px ${colors.shadow}`
        },
        lfdKeyTitle: { fontSize: '16px', fontWeight: 'bold', color: colors.text, marginBottom: '10px' },
        lfdKeyGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' },
        lfdKeyItem: { display: 'flex', alignItems: 'center', gap: '10px' },
        lfdKeyColor: { width: '20px', height: '20px', borderRadius: '4px', border: `1px solid ${colors.border}` },
        lfdKeyText: { fontSize: '14px', color: colors.text },
        flightBadge: {
            marginTop: '6px',
            padding: '3px 6px',
            borderRadius: '10px',
            backgroundColor: '#007bff',
            color: 'white',
            fontSize: '10px',
            fontWeight: 'bold',
            letterSpacing: '0.3px'
        },
        aircraftTrackerLink: {
            textDecoration: 'none',
            color: 'inherit',
            cursor: 'pointer'
        }
    };
};

// Add CSS for pulse animation
const pulseKeyframes = `
    @keyframes pulse {
        0% { opacity: 1; }
        50% { opacity: 0.5; }
        100% { opacity: 1; }
    }
`;

// Inject the CSS
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = pulseKeyframes;
    document.head.appendChild(style);
}

const normalizeAircraftIdentifier = (value) => {
    if (!value) return '';
    return String(value).trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
};

const getAircraftKey = (aircraft) => {
    if (!aircraft) return '';
    return String(aircraft.aircraft || aircraft.buno || '').trim();
};

const getAdsbIdentifierCandidates = (aircraft) => {
    const rawCandidates = [
        aircraft.buno,
        aircraft.callsign,
        aircraft.registration,
        aircraft.hex_id
    ];

    const candidates = new Set();
    rawCandidates.forEach(value => {
        const normalized = normalizeAircraftIdentifier(value);
        if (!normalized) return;
        candidates.add(normalized);

        const digits = normalized.replace(/\D/g, '');
        if (digits.length >= 6) {
            candidates.add(digits);
        }
    });

    return candidates;
};

const isAdsbAircraftInFlight = (aircraft) => {
    if (aircraft.is_on_ground !== null && aircraft.is_on_ground !== undefined) {
        return !aircraft.is_on_ground;
    }

    if (aircraft.altitude !== null && aircraft.altitude !== undefined) {
        return Number(aircraft.altitude) >= 100;
    }

    if (aircraft.ground_speed !== null && aircraft.ground_speed !== undefined) {
        return Number(aircraft.ground_speed) > 50;
    }

    return false;
};


function AircraftSummaryPage({ theme }) {
    const styles = getStyles(theme);
    const navigate = useNavigate();
    const [aircraftData, setAircraftData] = useState([]);
    const [adsbAircraft, setAdsbAircraft] = useState([]);
    const [sites, setSites] = useState([]);
    const [tmsList, setTmsList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const [siteFilter, setSiteFilter] = useState('');
    const [tmsFilter, setTmsFilter] = useState('');
    const [expandedStatuses, setExpandedStatuses] = useState(new Set(['UP', 'PMC', 'XC', 'DN', 'DS', 'PM', 'FCF', 'AWP', 'OTH', 'DET', 'AIMD', 'ACI', 'REI']));
    const { isStaff, groups } = useAuth();

    const statusOptions = ['UP', 'PMC', 'XC', 'DN', 'DS', 'PM', 'FCF', 'AWP', 'OTH', 'DET', 'AIMD', 'ACI', 'REI'];

    // WebSocket connection for real-time updates
    const wsUrl = useMemo(() => {
        const path = '/ws/aircraft-status/';
        const envWsBase = process.env.REACT_APP_WS_BASE_URL;
        if (envWsBase) {
            try {
                const base = envWsBase.endsWith('/') ? envWsBase.slice(0, -1) : envWsBase;
                if (base.startsWith('ws://') || base.startsWith('wss://')) {
                    return `${base}${path}`;
                }
                if (base.startsWith('http://') || base.startsWith('https://')) {
                    const u = new URL(base);
                    const proto = u.protocol === 'https:' ? 'wss' : 'ws';
                    return `${proto}://${u.host}${path}`;
                }
                const protoFromPage = window.location.protocol === 'https:' ? 'wss' : 'ws';
                return `${protoFromPage}://${base}${path}`;
            } catch (_) {}
        }
        const apiBase = process.env.REACT_APP_API_BASE_URL;
        if (apiBase && (apiBase.startsWith('http://') || apiBase.startsWith('https://'))) {
            try {
                const u = new URL(apiBase);
                const proto = u.protocol === 'https:' ? 'wss' : 'ws';
                return `${proto}://${u.host}${path}`;
            } catch (_) {}
        }
        const protoFromPage = window.location.protocol === 'https:' ? 'wss' : 'ws';
        const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
        if (isLocal) {
            return `${protoFromPage}://${window.location.hostname}:8000${path}`;
        }
        // For production, use the same host as the current page but with wss protocol
        return `${protoFromPage}://mets-backend.onrender.com${path}`;
    }, []); // Memoize the base URL to prevent re-creation on every render
    
    const { isConnected, lastMessage, error: wsError } = useWebSocket(wsUrl);

    const fetchAircraftData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/aircraft-status/');
            let aircraftList = response.data;
            
            // Handle paginated response
            if (response.data.results) {
                aircraftList = response.data.results;
            }
            
            // Ensure we have an array
            const aircraftArray = Array.isArray(aircraftList) ? aircraftList : [];
            setAircraftData(aircraftArray);
        } catch (error) {
            console.error('Error fetching aircraft data:', error);
            setAlert({ show: true, message: 'Failed to fetch aircraft data.', type: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchAdsbAircraft = useCallback(async () => {
        try {
            const merged = await fetchMergedAdsbAircraft();
            setAdsbAircraft(merged);
        } catch (error) {
            console.error('Error fetching ADS-B aircraft data:', error);
            setAdsbAircraft([]);
        }
    }, []);

    const fetchSites = useCallback(async () => {
        try {
            const response = await api.get('/sites/');
            let sitesData = response.data;
            
            // Handle paginated response
            if (response.data.results) {
                sitesData = response.data.results;
            }
            
            // Ensure we have an array
            const sitesArray = Array.isArray(sitesData) ? sitesData : [];
            setSites(sitesArray);
        } catch (error) {
            console.error('Error fetching sites:', error);
            setSites([]);
        }
    }, []);

    const fetchTms = useCallback(async () => {
        try {
            const response = await api.get('/tms/');
            const tmsData = Array.isArray(response.data) ? response.data : (response.data.results || []);
            setTmsList(tmsData);
        } catch (error) {
            console.error('Error fetching TMS:', error);
            setTmsList([]);
        }
    }, []);

    // Handle WebSocket messages for real-time updates
    useEffect(() => {
        if (lastMessage) {
            console.log('WebSocket message received:', lastMessage.type, lastMessage.data);
            switch (lastMessage.type) {
                case 'aircraft_data':
                    // Initial data from WebSocket
                    setAircraftData(lastMessage.data);
                    setLoading(false);
                    break;
                case 'aircraft_update':
                case 'aircraft_status_update':
                    // Single aircraft updated (handle both message types for compatibility)
                    const updatedAircraft = lastMessage.data;
                    setAircraftData(prevData => {
                        const updatedKey = getAircraftKey(updatedAircraft);
                        if (updatedAircraft.deleted) {
                            return prevData.filter(ac => getAircraftKey(ac) !== updatedKey);
                        }

                        const existingIndex = prevData.findIndex(ac => getAircraftKey(ac) === updatedKey);
                        if (existingIndex >= 0) {
                            const newData = [...prevData];
                            newData[existingIndex] = {
                                ...newData[existingIndex],
                                ...updatedAircraft
                            };
                            return newData;
                        }

                        return updatedKey ? [...prevData, updatedAircraft] : prevData;
                    });
                    break;
                case 'aircraft_created':
                    setAircraftData(prevData => {
                        const createdKey = getAircraftKey(lastMessage.data);
                        if (!createdKey) return prevData;

                        const existingIndex = prevData.findIndex(ac => getAircraftKey(ac) === createdKey);
                        if (existingIndex >= 0) {
                            const newData = [...prevData];
                            newData[existingIndex] = {
                                ...newData[existingIndex],
                                ...lastMessage.data
                            };
                            return newData;
                        }

                        return [...prevData, lastMessage.data];
                    });
                    break;
                case 'aircraft_deleted':
                    setAircraftData(prevData => {
                        const deletedKey = getAircraftKey(lastMessage.data);
                        return prevData.filter(aircraft => getAircraftKey(aircraft) !== deletedKey);
                    });
                    break;
                default:
                    console.log('Unknown WebSocket message type:', lastMessage.type);
                    break;
            }
        }
    }, [lastMessage]);

    // Show WebSocket connection status
    useEffect(() => {
        if (wsError) {
            console.error('WebSocket error details:', wsError);
            if (wsError.includes('Authentication failed')) {
                setAlert({ show: true, message: 'Authentication expired. Please refresh the page to log in again.', type: 'error' });
            } else {
                setAlert({ show: true, message: `Real-time updates unavailable: ${wsError}. Data will refresh manually.`, type: 'warning' });
            }
        } else if (isConnected) {
            console.log('WebSocket connected successfully to Aircraft Summary page');
            setAlert({ show: true, message: 'Real-time updates connected successfully!', type: 'success' });
        }
    }, [isConnected, wsError]);

    useEffect(() => {
        // Only fetch initial data if WebSocket is not connected
        // WebSocket will provide the initial data when connected
        if (!isConnected) {
            fetchAircraftData();
        }
        
        fetchSites();
        fetchTms();
        fetchAdsbAircraft();
    }, [isConnected, fetchAircraftData, fetchSites, fetchTms, fetchAdsbAircraft]);

    useEffect(() => {
        const intervalId = setInterval(fetchAdsbAircraft, 5000);
        return () => clearInterval(intervalId);
    }, [fetchAdsbAircraft]);

    // Auto-filter by site/TMS if user has exactly one matching group policy
    useEffect(() => {
        if (!isStaff && groups.length > 0 && sites.length > 0) {
            const siteGroups = groups.filter((group) =>
                sites.some((site) => site.name === group)
            );
            if (siteGroups.length === 1 && siteFilter === '') {
                setSiteFilter(siteGroups[0]);
            }
        }
    }, [groups, isStaff, siteFilter, sites]);

    useEffect(() => {
        if (!isStaff && groups.length > 0 && tmsList.length > 0) {
            const tmsGroups = getTmsGroups(groups, tmsList);
            if (tmsGroups.length === 1 && tmsFilter === '') {
                setTmsFilter(tmsGroups[0]);
            }
        }
    }, [groups, isStaff, tmsFilter, tmsList]);

    // Set up periodic refresh when WebSocket is not connected
    useEffect(() => {
        let intervalId;
        if (!isConnected) {
            // Refresh data every 30 seconds when WebSocket is not connected
            intervalId = setInterval(() => {
                console.log('Periodic refresh due to WebSocket disconnection');
                fetchAircraftData();
            }, 30000);
        }
        
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [isConnected, fetchAircraftData]);

    // Filter aircraft data based on site/TMS filters and user groups
    const filteredAircraftData = aircraftData.filter(aircraft => {
        // Apply site filter if selected
        if (siteFilter && aircraft.site !== siteFilter) {
            return false;
        }
        if (tmsFilter && getAircraftTmsName(aircraft) !== tmsFilter) {
            return false;
        }

        return aircraftMatchesGroupPolicy(aircraft, { isStaff, groups, sites, tmsList });
    });

    const filteredTmsOptions = (() => {
        const tmsGroups = getTmsGroups(groups, tmsList);
        if (isStaff || tmsGroups.length === 0) return tmsList;
        return tmsList.filter((tms) => tmsGroups.includes(tms.name));
    })();

    // Group filtered aircraft by status
    const groupedAircraft = filteredAircraftData.reduce((groups, aircraft) => {
        const status = aircraft.status || 'UNKNOWN';
        if (!groups[status]) {
            groups[status] = [];
        }
        groups[status].push(aircraft);
        return groups;
    }, {});

    // Calculate totals based on filtered data
    const totalAircraft = filteredAircraftData.length;
    const inFlightAircraftKeys = adsbAircraft.reduce((keys, aircraft) => {
        if (!isAdsbAircraftInFlight(aircraft)) return keys;

        getAdsbIdentifierCandidates(aircraft).forEach(candidate => keys.add(candidate));
        return keys;
    }, new Set());
    const isAircraftInFlight = (aircraft) => {
        const buno = normalizeAircraftIdentifier(aircraft.buno);
        return buno ? inFlightAircraftKeys.has(buno) : false;
    };
    const inFlightTotal = filteredAircraftData.filter(isAircraftInFlight).length;
    const statusTotals = statusOptions.reduce((totals, status) => {
        totals[status] = groupedAircraft[status]?.length || 0;
        return totals;
    }, {});
    const mcrsStatuses = ['UP', 'PMC', 'XC', 'DN', 'DS', 'PM', 'FCF'];
    const oorStatuses = ['AWP', 'OTH', 'AIMD', 'ACI', 'REI'];
    const mcrsTotal = mcrsStatuses.reduce((sum, s) => sum + (statusTotals[s] || 0), 0);
    const oorTotal = oorStatuses.reduce((sum, s) => sum + (statusTotals[s] || 0), 0);
    const tarTotal = Math.floor(mcrsTotal * 0.75);
    
    const isOthDepo = (ac) => ac.status === 'OTH' && ac.oth_sub_status === 'DEPO';
    const depoCount = filteredAircraftData.filter(isOthDepo).length;

    // Calculate A30 Status totals (excluding ACI and OTH/DEPO)
    const a30StatusOptions = statusOptions.filter(status => status !== 'ACI');
    const totalA30Aircraft = totalAircraft - (statusTotals['ACI'] || 0) - depoCount;

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            // Parse date string manually to avoid timezone issues
            // Expected format: "YYYY-MM-DD" or ISO string
            const dateParts = dateString.split('T')[0].split('-');
            if (dateParts.length === 3) {
                const year = parseInt(dateParts[0]);
                const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
                const day = parseInt(dateParts[2]);
                const date = new Date(year, month, day); // Create date in local timezone
                return date.toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: '2-digit', 
                    year: '2-digit' 
                });
            }
            // Fallback to original method if format is unexpected
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: '2-digit', 
                year: '2-digit' 
            });
        } catch {
            return 'N/A';
        }
    };

    const getStatusColor = (status) => {
        const statusColors = {
            UP: '#28a745',
            PMC: '#0070c0',
            XC: '#20c997',
            DN: '#dc3545',
            DS: '#dc3545',
            PM: '#ffc107',
            FCF: '#8716bbff',
            AWP: '#335d80ff',
            OTH: '#6c757d',
            DET: '#6c757d',
            AIMD: '#6c757d',
            ACI: '#6c757d',
            REI: '#6c757d'
        };
        return statusColors[status] || '#6c757d';
    };

    const getAircraftItemStyle = (aircraft) => {
        if (!aircraft.last_fly_date) {
            return {
                itemStyle: styles.aircraftItem,
                bunoStyle: styles.buno,
                lfdStyle: styles.lfd
            };
        }

        // Parse date manually to avoid timezone issues
        const dateParts = aircraft.last_fly_date.split('T')[0].split('-');
        let flyDate;
        if (dateParts.length === 3) {
            const year = parseInt(dateParts[0]);
            const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
            const day = parseInt(dateParts[2]);
            flyDate = new Date(year, month, day); // Create date in local timezone
        } else {
            flyDate = new Date(aircraft.last_fly_date);
        }
        
        const today = new Date();
        // Reset today to midnight for accurate day calculation
        const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const flyDateMidnight = new Date(flyDate.getFullYear(), flyDate.getMonth(), flyDate.getDate());
        const daysSinceFly = Math.floor((todayMidnight - flyDateMidnight) / (1000 * 60 * 60 * 24));

        // Critical: More than 90 days (bright cyan/teal)
        if (daysSinceFly > 90) {
            return {
                itemStyle: styles.aircraftItemCritical,
                bunoStyle: styles.bunoCritical,
                lfdStyle: styles.lfdCritical
            };
        }
        // Highlighted: 60 to 89 days (red)
        else if (daysSinceFly > 60) {
            return {
                itemStyle: styles.aircraftItemHighlighted,
                bunoStyle: styles.bunoHighlighted,
                lfdStyle: styles.lfdHighlighted
            };
        }
        // Warning: 30 to 59 days (yellow)
        else if (daysSinceFly > 30) {
            return {
                itemStyle: styles.aircraftItemWarning,
                bunoStyle: styles.bunoWarning,
                lfdStyle: styles.lfdWarning
            };
        }
        
        // Normal: Less than 30 days
        return {
            itemStyle: styles.aircraftItem,
            bunoStyle: styles.buno,
            lfdStyle: styles.lfd
        };
    };

    const renderAircraftCard = (aircraft) => {
        const aircraftStyles = getAircraftItemStyle(aircraft);
        const inFlight = isAircraftInFlight(aircraft);
        const itemStyle = inFlight
            ? {
                ...aircraftStyles.itemStyle,
                border: '2px solid #007bff',
                boxShadow: '0 0 0 2px rgba(0, 123, 255, 0.15)'
            }
            : aircraftStyles.itemStyle;
        const statusUrl = `/status-board?buno=${encodeURIComponent(aircraft.buno)}`;
        const openStatus = () => navigate(statusUrl);

        return (
            <div
                key={aircraft.buno}
                style={{ ...itemStyle, ...styles.aircraftTrackerLink }}
                role="link"
                tabIndex={0}
                title={`Open ${aircraft.buno} on Aircraft Status`}
                onClick={openStatus}
                onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openStatus();
                    }
                }}
            >
                <div style={aircraftStyles.bunoStyle}>{aircraft.buno}</div>
                <div style={aircraftStyles.lfdStyle}>
                    {formatDate(aircraft.last_fly_date)}
                </div>
                {inFlight && (
                    <Link
                        to={`/control-tower?aircraft=${encodeURIComponent(aircraft.buno)}`}
                        style={{ ...styles.flightBadge, textDecoration: 'none' }}
                        title={`View ${aircraft.buno} on Control Tower`}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                    >
                        IN FLIGHT
                    </Link>
                )}
            </div>
        );
    };

    const statusBoardUrl = (status, { excludeDepo = false } = {}) => {
        const params = new URLSearchParams();
        params.set('status', status);
        if (siteFilter) params.set('site', siteFilter);
        if (excludeDepo) params.set('excludeDepo', '1');
        return `/status-board?${params.toString()}`;
    };

    const renderStatusCountLink = (status, count, { excludeDepo = false } = {}) => {
        const siteLabel = siteFilter ? `${siteFilter} ` : '';
        const depoLabel = excludeDepo ? ' excluding DEPO' : '';
        return (
            <Link
                to={statusBoardUrl(status, { excludeDepo })}
                style={{
                    ...styles.totalNumber,
                    display: 'block',
                    color: getStatusColor(status),
                    textDecoration: 'none',
                    cursor: 'pointer'
                }}
                title={`Open ${siteLabel}${status} aircraft${depoLabel} on Aircraft Status`}
            >
                {count}
            </Link>
        );
    };

    const toggleStatus = (status) => {
        setExpandedStatuses(prev => {
            const newSet = new Set(prev);
            if (newSet.has(status)) {
                newSet.delete(status);
            } else {
                newSet.add(status);
            }
            return newSet;
        });
    };

    const getCategoryForStatus = (status) => {
        if (mcrsStatuses.includes(status)) {
            return { name: 'MCRS', total: mcrsTotal };
        } else if (oorStatuses.includes(status)) {
            return { name: 'OOR', total: oorTotal };
        }
        return null;
    };

    if (loading) {
        return (
            <div style={styles.page}>
                <div style={styles.container}>
                    <Spinner />
                </div>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />
                
                <div style={styles.pageHeader}>
                    <HamburgerMenu theme={theme} />
                    <h1 style={styles.pageTitle}>Aircraft Summary</h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {/* WebSocket Connection Status */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            fontSize: '12px',
                            color: isConnected ? '#28a745' : '#dc3545',
                            fontWeight: 'bold'
                        }}>
                            <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: isConnected ? '#28a745' : '#dc3545',
                                animation: isConnected ? 'pulse 2s infinite' : 'none'
                            }}></div>
                            {isConnected ? 'Live' : 'Offline'}
                        </div>
                        <div style={{ width: '20px' }}></div>
                    </div>
                </div>

                {/* Group Policy Indicator */}
                {!isStaff && !groups.includes('Supervisor') && groups.length > 0 && (
                    <div style={{
                        fontSize: '14px', 
                        color: styles.subtleText, 
                        marginBottom: '20px',
                        fontStyle: 'italic'
                    }}>
                        Showing aircraft for: {getGroupPolicyLabel(groups, sites, tmsList)}
                    </div>
                )}

                {isStaff && (
                    <div style={{
                        fontSize: '14px', 
                        color: styles.subtleText, 
                        marginBottom: '20px',
                        fontStyle: 'italic'
                    }}>
                        Showing all aircraft (Staff access)
                    </div>
                )}
                {groups.includes('Supervisor') && !isStaff && (
                    <div style={{
                        fontSize: '14px', 
                        color: styles.subtleText, 
                        marginBottom: '20px',
                        fontStyle: 'italic'
                    }}>
                        Showing all aircraft (Supervisor access)
                    </div>
                )}

                {/* Site / TMS Filters */}
                <div style={{
                    display: 'flex',
                    gap: '15px',
                    marginBottom: '20px',
                    alignItems: 'center',
                    flexWrap: 'wrap'
                }}>
                    <label style={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: styles.text
                    }}>
                        Filter by Site:
                    </label>
                    <select
                        value={siteFilter}
                        onChange={(e) => setSiteFilter(e.target.value)}
                        style={{
                            padding: '8px 12px',
                            borderRadius: '4px',
                            border: `1px solid ${styles.border}`,
                            backgroundColor: styles.cardBg,
                            color: styles.text,
                            fontSize: '14px',
                            minWidth: '150px'
                        }}
                    >
                        <option value="">All Sites</option>
                        {Array.isArray(sites) && sites.map(site => (
                            <option key={site.id} value={site.name}>{site.name}</option>
                        ))}
                    </select>
                    <label style={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: styles.text
                    }}>
                        Filter by TMS:
                    </label>
                    <select
                        value={tmsFilter}
                        onChange={(e) => setTmsFilter(e.target.value)}
                        style={{
                            padding: '8px 12px',
                            borderRadius: '4px',
                            border: `1px solid ${styles.border}`,
                            backgroundColor: styles.cardBg,
                            color: styles.text,
                            fontSize: '14px',
                            minWidth: '150px'
                        }}
                    >
                        <option value="">All TMS</option>
                        {Array.isArray(filteredTmsOptions) && filteredTmsOptions.map((tms) => (
                            <option key={tms.id || tms.name} value={tms.name}>{tms.name}</option>
                        ))}
                    </select>
                    {(siteFilter || tmsFilter) && (
                        <button
                            onClick={() => {
                                setSiteFilter('');
                                setTmsFilter('');
                            }}
                            style={{
                                padding: '6px 12px',
                                fontSize: '12px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Clear Filter
                        </button>
                    )}
                </div>

                {/* LFD Color Key */}
                <div style={styles.lfdKey}>
                    <div style={styles.lfdKeyTitle}>Last Fly Date (LFD) Color Legend</div>
                    <div style={styles.lfdKeyGrid}>
                        <div style={styles.lfdKeyItem}>
                            <div style={{
                                ...styles.lfdKeyColor,
                                backgroundColor: styles.aircraftItem.backgroundColor,
                                borderColor: styles.aircraftItem.borderColor || styles.border
                            }}></div>
                            <div style={styles.lfdKeyText}>Normal: 0-30 days since last flight</div>
                        </div>
                        <div style={styles.lfdKeyItem}>
                            <div style={{
                                ...styles.lfdKeyColor,
                                backgroundColor: styles.aircraftItemWarning.backgroundColor,
                                borderColor: '#ff9800'
                            }}></div>
                            <div style={styles.lfdKeyText}>Warning: 30-59 days since last flight</div>
                        </div>
                        <div style={styles.lfdKeyItem}>
                            <div style={{
                                ...styles.lfdKeyColor,
                                backgroundColor: styles.aircraftItemHighlighted.backgroundColor,
                                borderColor: '#f44336'
                            }}></div>
                            <div style={styles.lfdKeyText}>Highlighted: 60-89 days since last flight</div>
                        </div>
                        <div style={styles.lfdKeyItem}>
                            <div style={{
                                ...styles.lfdKeyColor,
                                backgroundColor: styles.aircraftItemCritical.backgroundColor,
                                borderColor: '#9c27b0'
                            }}></div>
                            <div style={styles.lfdKeyText}>Critical: 90+ days since last flight</div>
                        </div>
                        <div style={styles.lfdKeyItem}>
                            <div style={{
                                ...styles.lfdKeyColor,
                                backgroundColor: '#007bff',
                                borderColor: '#0056b3'
                            }}></div>
                            <div style={styles.lfdKeyText}>In Flight: matched to live ADS-B tracker</div>
                        </div>
                    </div>
                </div>

                {/* Total Summary */}
                <div style={styles.totalSummary}>
                    <div style={styles.totalTitle}>Total Aircraft: {totalAircraft}</div>
                    <div style={styles.totalGrid}>
                        {statusOptions.map(status => {
                            const count = statusTotals[status];
                            const percentage = totalAircraft > 0 ? ((count / totalAircraft) * 100).toFixed(1) : '0.0';
                            return (
                                <div key={status} style={styles.totalItem}>
                                    {renderStatusCountLink(status, count)}
                                    <div style={styles.totalPercentage}>
                                        {percentage}%
                                    </div>
                                    <div style={styles.totalLabel}>{status}</div>
                                </div>
                            );
                        })}
                        <div style={styles.totalItem}>
                            <div style={{...styles.totalNumber, color: '#007bff'}}>
                                {inFlightTotal}
                            </div>
                            <div style={styles.totalPercentage}>
                                {totalAircraft > 0 ? ((inFlightTotal / totalAircraft) * 100).toFixed(1) : '0.0'}%
                            </div>
                            <div style={styles.totalLabel}>In Flight</div>
                        </div>
                    </div>
                </div>
                {/* A30 Status Summary (excluding ACI) */}
                <div style={styles.totalSummary}>
                    <div style={styles.totalTitle}>A30 Status: {totalA30Aircraft}</div>
                    <div style={styles.totalGrid}>
                        {a30StatusOptions.map(status => {
                            const count = status === 'OTH'
                                ? (statusTotals[status] || 0) - depoCount
                                : statusTotals[status];
                            const percentage = totalA30Aircraft > 0 ? ((count / totalA30Aircraft) * 100).toFixed(1) : '0.0';
                            return (
                                <div key={status} style={styles.totalItem}>
                                    {renderStatusCountLink(status, count, { excludeDepo: status === 'OTH' })}
                                    <div style={styles.totalPercentage}>
                                        {percentage}%
                                    </div>
                                    <div style={styles.totalLabel}>{status}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div style={styles.totalSummary}>
                    <div style={styles.totalTitle}>Readiness Summary</div>
                    <div style={styles.totalGrid}>
                        {[{ label: 'MCRS', value: mcrsTotal }, { label: 'OOR', value: oorTotal }, { label: 'TAR', value: tarTotal }].map(item => {
                            const percentage = totalAircraft > 0 ? ((item.value / totalAircraft) * 100).toFixed(1) : '0.0';
                            return (
                                <div key={item.label} style={styles.totalItem}>
                                    <div style={styles.totalNumber}>
                                        {item.value}
                                    </div>
                                    <div style={styles.totalPercentage}>
                                        {percentage}%
                                    </div>
                                    <div style={styles.totalLabel}>{item.label}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                {/* Aircraft Status Sections */}
                {statusOptions.filter(status => status !== 'DET').map(status => {
                    const aircraftInStatus = groupedAircraft[status] || [];
                    const count = aircraftInStatus.length;
                    const percentage = totalAircraft > 0 ? ((count / totalAircraft) * 100).toFixed(1) : '0.0';
                    const isExpanded = expandedStatuses.has(status);
                    const category = getCategoryForStatus(status);
                    const categoryPercentage = category && category.total > 0 
                        ? ((count / category.total) * 100).toFixed(1) 
                        : '0.0';
                    
                    return (
                        <div key={status} style={styles.statusSection}>
                            <div 
                                style={{
                                    ...styles.statusHeader,
                                    backgroundColor: getStatusColor(status)
                                }}
                                onClick={() => toggleStatus(status)}
                            >
                                <div style={styles.statusHeaderLeft}>
                                    <span style={{
                                        ...styles.caret,
                                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
                                    }}>
                                        ▼
                                    </span>
                                    <div style={styles.statusTitle}>
                                        Total {status}
                                    </div>
                                </div>
                                <div style={styles.statusHeaderMiddle}>
                                    {count} of {totalAircraft} - {percentage}%
                                </div>
                                {category && (
                                    <div style={styles.statusHeaderRight}>
                                        {categoryPercentage}% of {category.total}
                                    </div>
                                )}
                            </div>
                            
                            {isExpanded && (
                                <div style={styles.aircraftGrid}>
                                    {aircraftInStatus.length > 0 ? (
                                        aircraftInStatus
                                            .sort((a, b) => a.buno.localeCompare(b.buno))
                                            .map(renderAircraftCard)
                                    ) : (
                                        <div style={styles.noAircraft}>
                                            No aircraft in {status} status
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
                {/* DET Status Section - Rendered at the bottom */}
                {(() => {
                    const status = 'DET';
                    const aircraftInStatus = groupedAircraft[status] || [];
                    const count = aircraftInStatus.length;
                    const percentage = totalAircraft > 0 ? ((count / totalAircraft) * 100).toFixed(1) : '0.0';
                    const isExpanded = expandedStatuses.has(status);
                    const category = getCategoryForStatus(status);
                    const categoryPercentage = category && category.total > 0 
                        ? ((count / category.total) * 100).toFixed(1) 
                        : '0.0';
                    
                    return (
                        <div key={status} style={styles.statusSection}>
                            <div 
                                style={{
                                    ...styles.statusHeader,
                                    backgroundColor: getStatusColor(status)
                                }}
                                onClick={() => toggleStatus(status)}
                            >
                                <div style={styles.statusHeaderLeft}>
                                    <span style={{
                                        ...styles.caret,
                                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
                                    }}>
                                        ▼
                                    </span>
                                    <div style={styles.statusTitle}>
                                        Total {status}
                                    </div>
                                </div>
                                <div style={styles.statusHeaderMiddle}>
                                    {count} of {totalAircraft} - {percentage}%
                                </div>
                                {category && (
                                    <div style={styles.statusHeaderRight}>
                                        {categoryPercentage}% of {category.total}
                                    </div>
                                )}
                            </div>
                            
                            {isExpanded && (
                                <div style={styles.aircraftGrid}>
                                    {aircraftInStatus.length > 0 ? (
                                        aircraftInStatus
                                            .sort((a, b) => a.buno.localeCompare(b.buno))
                                            .map(renderAircraftCard)
                                    ) : (
                                        <div style={styles.noAircraft}>
                                            No aircraft in {status} status
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })()}

            </div>
        </div>
    );
}

export default AircraftSummaryPage;
